import { useState, useMemo, useCallback } from 'react';
import { X } from 'lucide-react';
import TimeBlocksPage from './TimeBlocksPage';
import TimeBlockTaskPanel from '../components/schedule/TimeBlockTaskPanel';
import TaskModal from '../components/TaskModal';
import { useTasks } from '../hooks/useTasks';
import { useGoogleCalendar } from '../hooks/useGoogleCalendar';
import { useProjects } from '../hooks/useProjects';
import { useGoals } from '../hooks/useGoals';
import { useAuth } from '../hooks/useAuth';
import FeatureOnboarding from '../components/common/FeatureOnboarding';

function getInternalUserName(displayName) {
  if (!displayName) return null;
  const firstName = displayName.split(' ')[0];
  if (firstName === 'Jason') return 'Jason';
  if (firstName === 'Matt' || firstName === 'Matthew') return 'Matt';
  return firstName;
}

export default function SchedulePage({ onAddBlock }) {
  const { user } = useAuth();
  const currentUserName = getInternalUserName(user?.displayName);

  // Task management
  const {
    tasks,
    completeTask,
    uncompleteTask,
    updateTask,
    addTask,
    assignToBlock,
    removeFromBlock
  } = useTasks(currentUserName);

  // Google Calendar for blocks
  const {
    events,
    isSignedIn: gcalSignedIn,
    getTimeBlocksForDate,
    createTimeBlock,
    TIME_BLOCK_NAMES
  } = useGoogleCalendar();

  // Projects and Goals for task modal
  const { projects } = useProjects();
  const { goals } = useGoals(currentUserName);

  // Selected block state
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [showMobilePanel, setShowMobilePanel] = useState(false);

  // Task modal state
  const [editingTask, setEditingTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);

  // Get tasks for selected block
  const blockTasks = useMemo(() => {
    if (!selectedBlock) return [];
    return tasks.filter(t => t.gcalTimeBlockId === selectedBlock.id);
  }, [tasks, selectedBlock]);

  // Get all blocks for today (for rescheduling)
  const todayBlocks = useMemo(() => {
    if (!selectedBlock) return [];
    const blockDate = selectedBlock.start?.dateTime?.split('T')[0] ||
      selectedBlock.start?.date;
    return events.filter(e => {
      const eventDate = e.start?.dateTime?.split('T')[0] || e.start?.date;
      return eventDate === blockDate && TIME_BLOCK_NAMES.some(name =>
        e.summary?.toLowerCase().includes(name.toLowerCase())
      );
    });
  }, [events, selectedBlock, TIME_BLOCK_NAMES]);

  // Handle block selection
  const handleBlockSelect = useCallback((block) => {
    setSelectedBlock(block);
    // On mobile, show the bottom sheet
    if (window.innerWidth < 768) {
      setShowMobilePanel(true);
    }
  }, []);

  // Handle closing the panel
  const handleClosePanel = useCallback(() => {
    setSelectedBlock(null);
    setShowMobilePanel(false);
  }, []);

  // Task operations
  const handleToggleComplete = useCallback(async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (task?.completedAt) {
      await uncompleteTask(taskId);
    } else {
      await completeTask(taskId);
    }
  }, [tasks, completeTask, uncompleteTask]);

  const handleEditTask = useCallback((task) => {
    setEditingTask(task);
    setShowTaskModal(true);
  }, []);

  const handleRescheduleTask = useCallback(async (taskId, blockId, blockName, blockDate) => {
    if (blockId) {
      await assignToBlock(taskId, blockId, blockName, blockDate);
    } else {
      await removeFromBlock(taskId);
    }
  }, [assignToBlock, removeFromBlock]);

  const handleDelegateTask = useCallback((task) => {
    // Open task modal with waiting info pre-filled so user can set delegation details
    setEditingTask({ ...task, waitingFor: task.waitingFor || 'pending' });
    setShowTaskModal(true);
  }, []);

  const handleAddTaskToBlock = useCallback(async (title, block) => {
    const blockDate = block.start?.dateTime?.split('T')[0] || block.start?.date;
    await addTask({
      title,
      scheduledDate: blockDate,
      gcalTimeBlockId: block.id,
      gcalTimeBlockName: block.summary
    });
  }, [addTask]);

  const handleSaveTask = useCallback(async (taskData) => {
    if (editingTask?.id) {
      await updateTask(editingTask.id, taskData);
    } else {
      await addTask(taskData);
    }
    setShowTaskModal(false);
    setEditingTask(null);
  }, [editingTask, updateTask, addTask]);

  // Get tasks for a block (for capacity calculation in TimeBlocksPage)
  const getTasksForBlock = useCallback((blockId) => {
    return tasks.filter(t => t.gcalTimeBlockId === blockId);
  }, [tasks]);

  return (
    <FeatureOnboarding feature="schedule">
    <div className="flex h-full overflow-hidden">
      {/* Timeline (left column on desktop, full width on mobile) */}
      <div className={`
        flex-1 min-w-0 overflow-y-auto
        ${selectedBlock && window.innerWidth >= 768 ? 'md:w-3/5' : 'w-full'}
      `}>
        <TimeBlocksPage
          onAddBlock={onAddBlock}
          onBlockSelect={handleBlockSelect}
          selectedBlockId={selectedBlock?.id}
          getTasksForBlock={getTasksForBlock}
        />
      </div>

      {/* Task panel (right column on desktop) */}
      {selectedBlock && (
        <div className="hidden md:block w-2/5 max-w-md border-l border-border h-full overflow-y-auto">
          <TimeBlockTaskPanel
            block={selectedBlock}
            tasks={blockTasks}
            allBlocks={todayBlocks}
            onClose={handleClosePanel}
            onToggleComplete={handleToggleComplete}
            onEditTask={handleEditTask}
            onRescheduleTask={handleRescheduleTask}
            onDelegateTask={handleDelegateTask}
            onAddTask={handleAddTaskToBlock}
          />
        </div>
      )}

      {/* Mobile bottom sheet */}
      {showMobilePanel && selectedBlock && (
        <div className="md:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={handleClosePanel}
          />

          {/* Bottom sheet */}
          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-surface-base rounded-t-2xl shadow-lg animate-in slide-in-from-bottom duration-300">
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-border rounded-full" />
            </div>

            <TimeBlockTaskPanel
              block={selectedBlock}
              tasks={blockTasks}
              allBlocks={todayBlocks}
              onClose={handleClosePanel}
              onToggleComplete={handleToggleComplete}
              onEditTask={handleEditTask}
              onRescheduleTask={handleRescheduleTask}
              onDelegateTask={handleDelegateTask}
              onAddTask={handleAddTaskToBlock}
              isMobile
            />
          </div>
        </div>
      )}

      {/* Task Modal */}
      <TaskModal
        isOpen={showTaskModal}
        onClose={() => {
          setShowTaskModal(false);
          setEditingTask(null);
        }}
        onSave={handleSaveTask}
        editItem={editingTask}
        currentUserName={currentUserName}
        projects={projects}
        goals={goals}
        gcalSignedIn={gcalSignedIn}
        getTimeBlocksForDate={getTimeBlocksForDate}
        createTimeBlock={createTimeBlock}
        timeBlockNames={TIME_BLOCK_NAMES}
        getTasksForBlock={getTasksForBlock}
      />
    </div>
    </FeatureOnboarding>
  );
}
